"use client";

import type React from "react";
import { Suspense } from "react";

import "./globals.css";
import { SessionProvider } from "next-auth/react";
import { usePathname } from "next/navigation";

import { ThemeProvider } from "@/components/theme-provider";
import Navbar from "@/components/navbar";
import Footer from "@/components/footer";
import { ChatbotFloatingButton } from "@/components/chatbot-floating-button";
import { MotionProvider } from "@/context/motion-context";
import { TranslationProvider } from "@/context/translation-context";
import { AuthProvider, useAuth } from "@/context/auth-context";
import { JourneyProvider } from "@/context/journey-context";
import { NotificationProvider } from "@/context/notification-context";
import { AdminNotificationProvider } from "@/context/admin-notification-context";
import { EmailVerification } from "@/components/email-verification";
import { TutorRedirectGuard } from "@/components/tutor-redirect-guard";
import { TutorApprovalOverlay } from "@/components/tutor-approval-overlay";
import { ForcePasswordChange } from "@/components/force-password-change";
import { DeletedAccountGuard } from "@/components/deleted-account-guard";
import { SessionProvider as UserSessionProvider } from "@/context/session-context";
import { CommandPalette } from "@/components/command-palette";
import { OnboardingGuard } from "@/components/onboarding-guard";
import { ProductHints } from "@/components/product-hints";
import { AppStoreBanner } from "@/components/BetaBanner";
import { AccountStatusBanner } from "@/components/account-status-banner";
import { SiteVisitTracker } from "@/components/site-visit-tracker";
import { AttributionCapture } from "@/components/attribution-capture";
import { GoogleAnalytics } from "@/components/google-analytics";


// Removed unused variable: inter

function AppContent({ children }: { children: React.ReactNode }) {
  const {
    needsEmailVerification,
    pendingUser,
    completeEmailVerification,
    cancelRegistration,
    user,
  } = useAuth();
  const pathname = usePathname();

  // Don't show email verification on certain pages
  const excludeEmailVerification = [
    "/reset-password",
    "/forgot-password",
    "/login",
    "/verify-email",
  ].some((path) => pathname.startsWith(path));

  const hideAppChrome = pathname.startsWith("/founding-beta") || pathname.startsWith("/account-deleted");

  if (needsEmailVerification && pendingUser && !excludeEmailVerification) {
    return (
      <EmailVerification
        email={pendingUser.email!}
        onCancel={cancelRegistration}
      />
    );
  }

  return (
    <TutorRedirectGuard>
      <div className={`flex flex-col min-w-0 w-full overflow-x-hidden bg-[#f3f4f6]${hideAppChrome ? "" : " pb-24 lg:pb-0"}`}>
        {!hideAppChrome && pathname !== "/" && <div data-app-chrome><AppStoreBanner /></div>}
        {!hideAppChrome && <div data-app-chrome><Navbar /></div>}
        {!hideAppChrome && <div data-app-chrome><AccountStatusBanner /></div>}
        <main className={`min-w-0 w-full overflow-x-hidden${hideAppChrome ? "" : " lg:pb-20"}`}>
          {children}
        </main>
        {!hideAppChrome && <div className="hidden sm:block" data-app-chrome><Footer /></div>}
        {!hideAppChrome && (
        <Suspense fallback={null}>
          <div data-app-chrome>
          <ChatbotFloatingButton />
          </div>
        </Suspense>
        )}
        <TutorApprovalOverlay />
        <ForcePasswordChange />
        <DeletedAccountGuard />
        {!hideAppChrome && <div data-app-chrome><CommandPalette /></div>}
        <OnboardingGuard />
        <div data-app-chrome><ProductHints /></div>
        <SiteVisitTracker />
        <AttributionCapture />
        <GoogleAnalytics />
      </div>
    </TutorRedirectGuard>
  );
}

const ClientLayout = ({
  children,
}: Readonly<{
  children: React.ReactNode;
}>) => {
  return (
    <SessionProvider refetchInterval={0} refetchOnWindowFocus>
      <UserSessionProvider>
        <ThemeProvider
          attribute="class"
          defaultTheme="light"
          forcedTheme="light"
          enableSystem={false}
          disableTransitionOnChange
        >
          <AuthProvider>
            <JourneyProvider>
            <TranslationProvider>
              <MotionProvider>
                <NotificationProvider>
                  <AdminNotificationProvider>
                    <AppContent>{children}</AppContent>
                  </AdminNotificationProvider>
                </NotificationProvider>
              </MotionProvider>
            </TranslationProvider>
            </JourneyProvider>
          </AuthProvider>
        </ThemeProvider>
      </UserSessionProvider>
    </SessionProvider>
  );
};

export default ClientLayout;
